import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';

const Container = styled(motion.div)`
  min-height: 100vh;
  background: var(--background-start);
  padding-top: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ForgotPasswordBox = styled.div`
  background: var(--glass-bg);
  backdrop-filter: blur(20px);
  border: 1px solid var(--border-color);
  border-radius: 15px;
  padding: 40px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h1`
  color: white;
  text-align: center;
  margin-bottom: 30px;
  font-size: 2rem;
  font-weight: 700;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  margin-bottom: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--gradient-pink);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  background: linear-gradient(90deg, var(--gradient-purple), var(--gradient-pink));
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(255, 0, 127, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const BackLink = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 20px;
  color: var(--secondary-color);
  text-decoration: none;
  
  &:hover {
    color: var(--gradient-cyan);
  }
`;

const Message = styled.div`
  color: ${props => props.success ? '#2ed573' : '#ff6b6b'};
  background: ${props => props.success ? 'rgba(46, 213, 115, 0.1)' : 'rgba(255, 107, 107, 0.1)'};
  border: 1px solid ${props => props.success ? 'rgba(46, 213, 115, 0.3)' : 'rgba(255, 107, 107, 0.3)'};
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 20px;
  font-size: 0.9rem;
  text-align: center;
`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  // Email formatını kontrol et
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setSuccess(false);

    // Email validasyonu
    if (!isValidEmail(email)) {
      setMessage('Lütfen geçerli bir email adresi girin');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.FORGOT_PASSWORD), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setMessage('Şifre sıfırlama linki email adresinize gönderildi. Lütfen email kutunuzu kontrol edin.');
      } else {
        setMessage(data.message || 'Bir hata oluştu');
      }
    } catch (error) {
      setMessage('Şifre sıfırlama isteği gönderilirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <Container
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <ForgotPasswordBox>
          <Title>Şifremi Unuttum</Title>
          <p style={{ color: 'var(--secondary-color)', textAlign: 'center', marginBottom: '20px' }}>
            Email adresinizi girin, size şifre sıfırlama linki gönderelim.
          </p>
          <form onSubmit={handleSubmit}>
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {message && <Message success={success}>{message}</Message>}
            <Button type="submit" disabled={loading || success}>
              {loading ? 'Gönderiliyor...' : success ? 'Email Gönderildi' : 'Şifre Sıfırlama Linki Gönder'}
            </Button>
          </form>
          <BackLink to="/login">
            Giriş sayfasına dön
          </BackLink>
        </ForgotPasswordBox>
      </Container>
    </>
  );
};

export default ForgotPassword;

